import type { Middleware, MiddlewareAPI, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { io, type Socket } from 'socket.io-client';
import {
  setDrones,
  updateDroneTelemetry,
  addDroneStream,
  removeDroneStream,
  updateDroneLastFrame,
  addDroneLog,
  addOrUpdateDrone,
  setDroneOffline,
  setConnectionStatus,
  updateDroneTriggerConfig,
  updateDroneControlMode,
  type ControllerInputData,
  type DroneControlMode,
  type IncomingDroneData,
} from '../features/droneLiveSlice';
import {
  simulationOwnershipChanged,
  simulationDecisionReceived,
  type SimulationOwnershipEvent,
  type SimulationDecisionEvent,
} from '../features/simulationLiveSlice';
import type { LiveTelemetry } from '../services/droneApi';
import type { TriggerTemplate } from '@/types';
import { getTokenRefreshManager, destroyTokenRefreshManager } from '@/utils/tokenRefreshManager';
import { getAccessToken } from '@/utils/auth';
import { authApi } from '../services/authApi';

let socket: Socket | null = null;
let tokenRefreshInitialized = false;

// Throttle map for last frame URL updates: key = "droneId:cameraId", value = last update timestamp
const lastFrameUpdateTimestamps: Map<string, number> = new Map();
const LAST_FRAME_THROTTLE_MS = 3000; // Update at most once per 3 seconds per camera

// Export function to get socket for direct access if needed
export const getSocket = () => socket;

export type AppCommandRejectReason =
  'invalid_payload' | 'payload_too_large' | 'rate_limited' | 'app_not_available' | 'drone_offline';

export type AppCommandAck =
  | { status: 'accepted' }
  | {
      status: 'rejected';
      reason: AppCommandRejectReason;
      message: string;
      retryAfterSeconds?: number;
    };

const APP_COMMAND_ACK_TIMEOUT_MS = 5000;

// Operator → drone-app command (SCRUM-1758). Binary leaves (ArrayBuffer) go as
// socket.io attachments. Throws when the socket is down or the ack times out —
// the outcome is then unknown.
export const emitAppCommand = (
  // no droneAppId: addressed to the app baked into the drone OS image
  metadata: { droneId: string; droneAppId?: string },
  payload: { type: string } & Record<string, unknown>,
): Promise<AppCommandAck> => {
  if (!socket?.connected) return Promise.reject(new Error('socket not connected'));
  return socket.timeout(APP_COMMAND_ACK_TIMEOUT_MS).emitWithAck('app-command', {
    metadata: { ...metadata, timestamp: Date.now() },
    payload,
  });
};

// Client-side camera/stream diagnostics surfaced in the System Logs panel.
const logCameraEvent = (
  store: MiddlewareAPI,
  droneId: string,
  level: 'INFO' | 'WARN' | 'ERROR',
  message: string,
) => {
  const timestamp = new Date().toISOString();
  const text = `[${level}] camera: ${message}`;
  store.dispatch(
    addDroneLog({
      droneId,
      log: {
        timestamp,
        message: text,
        time: new Date(timestamp).toLocaleTimeString(),
        log: text,
        level,
        metadata: { source: 'web-client' },
      },
    }),
  );
};

const streamCount = (store: MiddlewareAPI, droneId: string): number =>
  (store.getState() as { droneLive: { drones: Record<string, { streams: unknown[] }> } }).droneLive
    .drones[droneId]?.streams.length ?? 0;

/**
 * Initialize token refresh manager to coordinate across tabs
 * and send new tokens to the WebSocket server
 */
const initializeTokenRefresh = (store: MiddlewareAPI) => {
  if (tokenRefreshInitialized) return;

  const manager = getTokenRefreshManager();

  manager.init({
    onTokenRefreshed: (newToken: string) => {
      // Send new token to WebSocket server if connected
      if (socket?.connected) {
        socket.emit('request:auth:refresh', { accessToken: newToken });
      }
    },
    refreshTokenFn: async (): Promise<string | null> => {
      try {
        // Use the RTK Query endpoint to refresh token. The middleware store's
        // dispatch is thunk-capable at runtime; MiddlewareAPI just types it as
        // the base Dispatch.
        const dispatch = store.dispatch as ThunkDispatch<unknown, unknown, UnknownAction>;
        await dispatch(authApi.endpoints.refreshToken.initiate()).unwrap();
        // After successful refresh, get the new token from cookie
        return getAccessToken();
      } catch (error) {
        console.error('[droneWebSocketMiddleware] Token refresh failed:', error);
        return null;
      }
    },
  });

  tokenRefreshInitialized = true;
};

/**
 * Cleanup token refresh manager
 */
const cleanupTokenRefresh = () => {
  if (tokenRefreshInitialized) {
    destroyTokenRefreshManager();
    tokenRefreshInitialized = false;
  }
};

export const droneWebSocketMiddleware: Middleware = (store) => {
  return (next) => (action) => {
    const result = next(action);

    // Type guard to check if action has a type property
    if (typeof action === 'object' && action !== null && 'type' in action) {
      const typedAction = action as { type: string; payload?: unknown };

      // Listen for specific actions to start/stop WebSocket connection
      if (typedAction.type === 'droneLive/startWebSocket') {
        if (socket?.connected) {
          return result;
        }

        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4444';

        socket = io(`${apiUrl}/drone-operator`, {
          transports: ['websocket'],
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10000,
          withCredentials: true,
        });

        // Initialize before the first connect so an auth-rejected handshake
        // can already trigger a token refresh
        initializeTokenRefresh(store);

        socket.on('connect', () => {
          store.dispatch(setConnectionStatus('connected'));
          // Re-sync auth after reconnects so the server tracks the current
          // token expiry for this socket
          const token = getAccessToken();
          if (token) {
            socket?.emit('request:auth:refresh', { accessToken: token });
          }
        });

        socket.on('disconnect', (reason) => {
          store.dispatch(setConnectionStatus('disconnected'));
          // socket.io never auto-reconnects after a server-initiated
          // disconnect (expired-token kick) — refresh and reconnect manually
          if (reason === 'io server disconnect') {
            void getTokenRefreshManager()
              .forceRefresh()
              .finally(() => socket?.connect());
          }
        });

        socket.on('connect_error', (error) => {
          console.error('WebSocket connection error:', error);
          store.dispatch(setConnectionStatus('error'));
          // Handshake may have been rejected due to an expired token —
          // refresh before the next automatic reconnection attempt
          void getTokenRefreshManager().forceRefresh();
        });

        // Initial state with all online drones
        socket.on('initial-state', (data: { drones: IncomingDroneData[] }) => {
          store.dispatch(setDrones(data.drones));
          data.drones.forEach((d) => {
            logCameraEvent(
              store,
              d.id,
              'INFO',
              `operator socket synced, ${d.streams?.length ?? 0} live stream(s)` +
                (d.streams?.length ? ` [${d.streams.map((s) => s.cameraId).join(', ')}]` : ''),
            );
          });
        });

        // Real-time telemetry updates (frames also carry the drone's controlMode)
        socket.on(
          'drone-telemetry-all',
          (data: {
            droneId: string;
            event: { data: LiveTelemetry };
            controlMode?: DroneControlMode;
          }) => {
            store.dispatch(
              updateDroneTelemetry({
                droneId: data.droneId,
                telemetry: data.event.data,
                controlMode: data.controlMode,
              }),
            );
          },
        );

        // Control mode changed (by any operator/tab, or auto-flip on manual input)
        socket.on('drone-control-mode', (data: { droneId: string; mode: DroneControlMode }) => {
          store.dispatch(updateDroneControlMode(data));
        });

        // Stream available (handle both event names for compatibility)
        socket.on(
          'stream-available',
          (data: {
            droneId: string;
            cameraId: number;
            streamUrl: string;
            streamPort: number;
            thumbnailUrl?: string;
          }) => {
            store.dispatch(
              addDroneStream({
                droneId: data.droneId,
                cameraId: data.cameraId.toString(),
                url: data.streamUrl,
              }),
            );
          },
        );

        // New event name: drone-stream-available
        socket.on(
          'drone-stream-available',
          (data: { droneId: string; sessionId?: string; cameraId: string; url: string }) => {
            store.dispatch(
              addDroneStream({
                droneId: data.droneId,
                cameraId: data.cameraId,
                url: data.url,
              }),
            );
            logCameraEvent(
              store,
              data.droneId,
              'INFO',
              `stream available cam=${data.cameraId} session=${data.sessionId ?? '-'} url=${data.url} (${streamCount(store, data.droneId)} live)`,
            );
          },
        );

        // Stream stopped (handle both event names)
        socket.on('stream-stopped', (data: { droneId: string; cameraId: number }) => {
          store.dispatch(
            removeDroneStream({
              droneId: data.droneId,
              cameraId: data.cameraId.toString(),
            }),
          );
        });

        socket.on('drone-stream-stopped', (data: { droneId: string; cameraId: string }) => {
          store.dispatch(
            removeDroneStream({
              droneId: data.droneId,
              cameraId: data.cameraId,
            }),
          );
          logCameraEvent(
            store,
            data.droneId,
            'WARN',
            `stream stopped cam=${data.cameraId} (${streamCount(store, data.droneId)} live left)`,
          );
        });

        // Last frame URL update (throttled to 1 update per second per camera)
        socket.on(
          'drone-last-frame-url',
          (data: { droneId: string; cameraId: string; url: string }) => {
            const key = `${data.droneId}:${data.cameraId}`;
            const now = Date.now();
            const lastUpdate = lastFrameUpdateTimestamps.get(key) || 0;

            // Only dispatch if enough time has passed since last update
            if (now - lastUpdate >= LAST_FRAME_THROTTLE_MS) {
              lastFrameUpdateTimestamps.set(key, now);
              store.dispatch(
                updateDroneLastFrame({
                  droneId: data.droneId,
                  cameraId: data.cameraId,
                  url: data.url,
                }),
              );
            }
          },
        );

        // Drone log
        socket.on(
          'drone-log',
          (data: {
            droneId: string;
            event?: {
              event?: string;
              metadata?: { timestamp?: number; session_id?: string };
              data?: { log?: string };
            };
            // Legacy format support
            timestamp?: string;
            level?: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG';
            message?: string;
            metadata?: unknown;
          }) => {
            // Handle new format with event wrapper
            if (data.event?.data?.log) {
              const timestamp = data.event.metadata?.timestamp
                ? new Date(data.event.metadata.timestamp).toISOString()
                : new Date().toISOString();
              const logMessage = data.event.data.log;

              // Extract log level from message if present (e.g., "[INFO]", "[ERROR]")
              let level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' = 'INFO';
              const levelMatch = logMessage.match(/\[(INFO|WARN|ERROR|DEBUG)\]/i);
              if (levelMatch) {
                level = levelMatch[1].toUpperCase() as 'INFO' | 'WARN' | 'ERROR' | 'DEBUG';
              }

              store.dispatch(
                addDroneLog({
                  droneId: data.droneId,
                  log: {
                    timestamp,
                    message: logMessage,
                    time: new Date(timestamp).toLocaleTimeString(),
                    log: logMessage,
                    level,
                    metadata: data.event.metadata,
                  },
                }),
              );
            } else if (data.message || data.timestamp) {
              // Legacy format
              store.dispatch(
                addDroneLog({
                  droneId: data.droneId,
                  log: {
                    timestamp: data.timestamp || new Date().toISOString(),
                    message: data.message || '',
                    time: new Date(data.timestamp || Date.now()).toLocaleTimeString(),
                    log: data.message || '',
                    level: data.level,
                    metadata: data.metadata,
                  },
                }),
              );
            }
          },
        );

        // Drone connected
        socket.on('drone-connected', (data: { drone: IncomingDroneData }) => {
          const before = streamCount(store, data.drone.id);
          store.dispatch(addOrUpdateDrone(data.drone));
          const after = streamCount(store, data.drone.id);
          logCameraEvent(
            store,
            data.drone.id,
            after < before ? 'WARN' : 'INFO',
            `drone (re)connected, streams ${before} -> ${after}` +
              (data.drone.streams ? '' : ' (payload had no streams field)'),
          );
        });

        // Drone disconnected
        socket.on('drone-disconnected', (data: { droneId: string }) => {
          const before = streamCount(store, data.droneId);
          store.dispatch(setDroneOffline(data.droneId));
          logCameraEvent(
            store,
            data.droneId,
            'WARN',
            `drone disconnected, ${before} stream(s) cleared`,
          );
        });

        // Trigger config update
        socket.on('trigger-config', (data: { droneId: string; config: TriggerTemplate | null }) => {
          store.dispatch(
            updateDroneTriggerConfig({
              droneId: data.droneId,
              config: data.config,
            }),
          );
        });

        // Simulation ownership lease changed (userId null == released)
        socket.on('simulation-ownership-changed', (data: SimulationOwnershipEvent) => {
          store.dispatch(simulationOwnershipChanged(data));
        });

        // Compact AI decision event, one per tick per team; full record via
        // GET /simulations/:id/decisions
        socket.on('simulation-agent-decision', (data: SimulationDecisionEvent) => {
          store.dispatch(simulationDecisionReceived(data));
        });
      }

      if (typedAction.type === 'droneLive/stopWebSocket') {
        if (socket) {
          socket.disconnect();
          socket = null;
          store.dispatch(setConnectionStatus('disconnected'));
          // Note: Don't cleanup token refresh here as other tabs might still use it
          // It will be cleaned up on logout
        }
      }

      // Handle auth clear (logout) - cleanup token refresh manager
      if (typedAction.type === 'auth/clearAuth') {
        cleanupTokenRefresh();
      }

      // Handle controller input - emit via WebSocket
      if (typedAction.type === 'droneLive/sendControllerInput' && socket?.connected) {
        const payload = typedAction.payload as ControllerInputData;
        socket.emit('controller-input', {
          metadata: { droneId: payload.droneId, timestamp: Date.now() },
          data: payload.data,
        });
      }

      // Handle control mode set - emit via WebSocket (re-arm / manual park)
      if (typedAction.type === 'droneLive/setDroneControlMode' && socket?.connected) {
        const payload = typedAction.payload as { droneId: string; mode: DroneControlMode };
        socket.emit('request:control-mode:set', payload);
      }

      // Handle drone command - emit via WebSocket
      if (typedAction.type === 'droneLive/sendDroneCommand' && socket?.connected) {
        const payload = typedAction.payload as {
          droneId: string;
          command: string;
          data?: Record<string, unknown>;
        };
        socket.emit('drone-command', {
          droneId: payload.droneId,
          command: payload.command,
          data: payload.data || {},
        });
      }
    }

    return result;
  };
};
